import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { validateImportFile, describeImportError } from "@/lib/importFileValidator";
import { formatDateTime, formatCurrency } from "@/lib/utils-date";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Upload, ShoppingCart, Trash2, Camera } from "lucide-react";
import SaleSheet from "@/components/SaleSheet";
import ReceiptScanner from "@/components/ReceiptScanner";
import ExportMenu from "@/components/ExportMenu";
import { usePerm } from "@/context/AuthContext";

export default function Sales() {
  const canWrite = usePerm("sales_write");
  const canScan = usePerm("scan_receipts");
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const dateFilter = searchParams.get("date"); // YYYY-MM-DD

  const load = async () => {
    const [s, p] = await Promise.all([api.listSales(500), api.listProducts()]);
    setSales(s);
    setProducts(p);
  };

  useEffect(() => {
    load();
  }, []);

  const visibleSales = useMemo(() => {
    if (!dateFilter) return sales;
    return sales.filter((s) => (s.sold_at || "").slice(0, 10) === dateFilter);
  }, [sales, dateFilter]);

  const clearDateFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("date");
    setSearchParams(next, { replace: true });
  };

  const formatItDate = (iso) => {
    try {
      const d = new Date(`${iso}T00:00:00`);
      return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    } catch {
      return iso;
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    const check = validateImportFile(file);
    if (!check.ok) {
      toast.error("File non valido", { description: check.message });
      return;
    }
    try {
      const res = await api.importSales(file);
      toast.success(`${res.inserted} vendite importate`, {
        description: res.total_errors ? `${res.total_errors} righe con errori` : "Tutto ok",
      });
      load();
    } catch (err) {
      toast.error("Errore import", { description: describeImportError(err) });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteSale(deleteTarget.id);
      toast.success("Vendita eliminata, stock ripristinato");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error("Errore", { description: err?.response?.data?.detail || err.message });
    }
  };

  const totalRevenue = visibleSales.reduce((sum, s) => sum + s.total, 0);

  return (
    <div data-testid="sales-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8E918C] mb-2">Registro</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Vendite
          </h1>
          <p className="text-[#5C5F5A] mt-2">
            {visibleSales.length} vendite · Totale {formatCurrency(totalRevenue)}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            ref={fileRef}
            data-testid="import-sales-input"
            type="file"
            /* Niente `accept`: vedi Products.jsx — su Android il picker
               nasconde i file per via del MIME non standard dei CSV. */
            className="hidden"
            onChange={handleImport}
          />
          {canScan && (
          <Button
            data-testid="scan-sale-btn"
            onClick={() => setScannerOpen(true)}
            variant="outline"
            className="border-[#C5E1D1] bg-[#E8F3EC] text-[#2B5A3F] hover:bg-[#D3E8DA]"
          >
            <Camera size={16} className="mr-2" />
            Scansiona scontrino
          </Button>
          )}
          {canWrite && (
          <Button
            data-testid="import-sales-btn"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            className="border-[#EAE7E1] bg-white hover:bg-[#F4F1ED]"
          >
            <Upload size={16} className="mr-2" />
            Importa Excel/CSV
          </Button>
          )}
          <ExportMenu
            testid="export-sales-btn"
            onExport={(fmt) => api.exportSales(fmt)}
            onExportPeriod={(fmt, days) => api.exportSales(fmt, days)}
            filenameBase="vendite"
            extraOptions={[
              { label: "Ultimi 7 giorni", days: 7 },
              { label: "Ultimi 30 giorni", days: 30 },
              { label: "Ultimi 90 giorni", days: 90 },
            ]}
          />
          {canWrite && (
          <Button
            data-testid="add-sale-btn"
            onClick={() => setOpen(true)}
            className="bg-[#2E4A3B] hover:bg-[#21382C] text-white"
          >
            <Plus size={16} className="mr-2" />
            Nuova vendita
          </Button>
          )}
        </div>
      </div>

      {/* Filtro data attivo (da Dashboard) */}
      {dateFilter && (
        <div
          data-testid="active-date-filter"
          className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-[#F4F1ED] border-[#EAE7E1] text-[#2E4A3B]"
        >
          <div className="text-sm font-medium">
            Filtro data: <span className="font-semibold capitalize">{formatItDate(dateFilter)}</span>
            <span className="ml-2 text-xs opacity-80">({visibleSales.length} {visibleSales.length === 1 ? "vendita" : "vendite"})</span>
          </div>
          <button
            type="button"
            data-testid="clear-date-filter-btn"
            onClick={clearDateFilter}
            className="text-xs underline hover:no-underline opacity-80 hover:opacity-100"
          >
            Mostra tutte
          </button>
        </div>
      )}

      {/* Mobile cards */}
      <div className="lg:hidden space-y-3">
        {visibleSales.length === 0 ? (
          <div className="mag-card p-12 text-center">
            <div className="flex flex-col items-center gap-3 text-[#8E918C]">
              <ShoppingCart size={32} />
              <div className="text-sm">{dateFilter ? "Nessuna vendita in questa data" : "Nessuna vendita registrata"}</div>
            </div>
          </div>
        ) : (
          visibleSales.map((s) => (
            <div
              key={s.id}
              data-testid={`sale-card-${s.id}`}
              className="mag-card p-4 space-y-2"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[#1A1C1A] text-base leading-tight">{s.product_name}</div>
                  <div className="text-xs text-[#8E918C] font-mono mt-0.5">{formatDateTime(s.sold_at)}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-base font-semibold text-[#2B5A3F] font-mono">{formatCurrency(s.total)}</div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-xs">
                {s.weight_sold ? (
                  <span className="inline-flex items-center gap-1 text-[#2B5A3F] bg-[#E8F3EC] border border-[#C5E1D1] px-2 py-0.5 rounded-md font-mono">
                    {s.weight_sold}
                  </span>
                ) : (
                  <span className="font-mono text-[#5C5F5A] bg-[#F4F1ED] border border-[#EAE7E1] px-2 py-0.5 rounded-md">
                    {s.quantity} pz
                  </span>
                )}
                <span className="font-mono text-[#5C5F5A] text-[11px]">
                  × {s.price_per_kg
                    ? `${formatCurrency(s.price_per_kg)}/kg`
                    : formatCurrency(s.unit_price)}
                </span>
                {s.batch && (
                  <span className="font-mono text-[#8E918C] text-[10px] bg-[#F4F1ED] border border-[#EAE7E1] px-1.5 py-0.5 rounded">
                    Lotto {s.batch}
                  </span>
                )}
              </div>

              <div className="flex justify-end pt-1 border-t border-[#EAE7E1]">
                {canWrite && (
                <Button
                  data-testid={`delete-sale-mobile-${s.id}`}
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeleteTarget(s)}
                  className="text-[#C65D47] hover:bg-[#FCEAE8] text-xs h-8"
                >
                  <Trash2 size={13} className="mr-1.5" />
                  Annulla vendita
                </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop table */}
      <div className="mag-card overflow-hidden hidden lg:block">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F4F1ED] hover:bg-[#F4F1ED] border-[#EAE7E1]">
                <TableHead className="text-[#5C5F5A] text-xs uppercase tracking-wider font-semibold">Data</TableHead>
                <TableHead className="text-[#5C5F5A] text-xs uppercase tracking-wider font-semibold">Prodotto</TableHead>
                <TableHead className="text-[#5C5F5A] text-xs uppercase tracking-wider font-semibold">Q.tà / Peso</TableHead>
                <TableHead className="text-[#5C5F5A] text-xs uppercase tracking-wider font-semibold">Prezzo</TableHead>
                <TableHead className="text-[#5C5F5A] text-xs uppercase tracking-wider font-semibold">Totale</TableHead>
                <TableHead className="text-[#5C5F5A] text-xs uppercase tracking-wider font-semibold text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleSales.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-16">
                    <div className="flex flex-col items-center gap-3 text-[#8E918C]">
                      <ShoppingCart size={32} />
                      <div className="text-sm">{dateFilter ? "Nessuna vendita in questa data" : "Nessuna vendita registrata"}</div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                visibleSales.map((s) => (
                  <TableRow
                    key={s.id}
                    data-testid={`sale-row-${s.id}`}
                    className="border-[#EAE7E1] hover:bg-[#F9F8F6]"
                  >
                    <TableCell className="text-sm text-[#5C5F5A] font-mono whitespace-nowrap">
                      {formatDateTime(s.sold_at)}
                    </TableCell>
                    <TableCell className="text-sm font-medium text-[#1A1C1A]">
                      <div>{s.product_name}</div>
                      {s.batch && (
                        <div className="text-[10px] text-[#8E918C] font-mono mt-0.5">Lotto: {s.batch}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm font-mono">
                      {s.weight_sold ? (
                        <span className="inline-flex items-center gap-1 text-[#2B5A3F] bg-[#E8F3EC] border border-[#C5E1D1] px-1.5 py-0.5 rounded-md text-xs">
                          {s.weight_sold}
                        </span>
                      ) : (
                        <span>{s.quantity} pz</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm font-mono">
                      {s.price_per_kg
                        ? `${formatCurrency(s.price_per_kg)}/kg`
                        : formatCurrency(s.unit_price)}
                    </TableCell>
                    <TableCell className="text-sm font-mono font-semibold text-[#2B5A3F]">
                      {formatCurrency(s.total)}
                    </TableCell>
                    <TableCell className="text-right">
                      {canWrite && (
                      <Button
                        data-testid={`delete-sale-${s.id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteTarget(s)}
                        className="h-8 w-8 text-[#C65D47] hover:bg-[#FCEAE8]"
                      >
                        <Trash2 size={14} />
                      </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <SaleSheet
        open={open}
        onOpenChange={setOpen}
        products={products}
        onSaved={() => {
          setOpen(false);
          load();
        }}
      />

      <ReceiptScanner
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        scanType="sale"
        products={products}
        onSaved={load}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annullare questa vendita?</AlertDialogTitle>
            <AlertDialogDescription>
              La vendita verrà rimossa e lo stock del prodotto verrà ripristinato.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-sale-confirm"
              onClick={handleDelete}
              className="bg-[#C65D47] hover:bg-[#A64B38]"
            >
              Conferma
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
